import { HugeiconsIcon } from "@hugeicons/react";
import { Chatting01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components/helpers/Show";
import { cx } from "shared/utils/classnames";

/**
 * The three states the design draws for an empty thread:
 *   - `compact` — no medallion, Body/Bold title. What /inbox has always shown.
 *   - `card`    — medallion + H4/Bold 20/32 on Content/500, Footnote/Medium copy.
 *   - `drawer`  — the same at the drawer's scale: H3/Bold 24/32, Body/Medium copy.
 */
export type MessagesEmptyStateVariant = "compact" | "card" | "drawer";

const variantStyles: Record<
  MessagesEmptyStateVariant,
  { title: string; description: string }
> = {
  compact: {
    title: "text-base text-content-1000",
    description: "max-w-[352px] text-sm"
  },
  card: {
    title: "text-xl leading-8 text-content-500",
    description: "max-w-[356px] text-sm"
  },
  drawer: {
    title: "text-2xl leading-8 text-content-500",
    description: "max-w-[400px] text-base"
  }
};

export const MessagesEmptyState = ({
  title = "No messages yet",
  description,
  variant = "compact"
}: {
  title?: string;
  description?: string;
  variant?: MessagesEmptyStateVariant;
}) => {
  const styles = variantStyles[variant];

  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <Show if={variant !== "compact"}>
        <span className="mb-6 flex size-20 shrink-0 items-center justify-center rounded-full bg-primary-50">
          <HugeiconsIcon
            icon={Chatting01Icon}
            size={40}
            className="text-primary-500"
          />
        </span>
      </Show>
      <p className={cx("font-bold", styles.title)}>{title}</p>
      {description ? (
        <p
          className={cx(
            "mt-2 font-medium text-content-300",
            styles.description
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
};
